import { Formik, Form } from 'formik';
import { observer } from 'mobx-react-lite';
import { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import CommonButton from '../../../../../../../components/common-components/button/CommonButton';
import Popup from '../../../../../../../components/common-components/pop-up/Popup';
import { FormikTextField } from '../../../../../../../components/form-components/common-components/text-field/FormikTextField';
import { FormikDropdown } from '../../../../../../../components/form-components/common-components/dropdown/FormikDropdown';
import { useStore } from '../../../../../../../hooks/useStore';
import QrOriginalData from '../qr-code/parts/QrOriginalData';
import styles from './NewRowFromKn.module.css';

const validationSchema = Yup.object({
  knNumber: Yup.string().required('Künye numarası gereklidir'),
});
const formValidationSchema = Yup.object({
  labelNo: Yup.number(),
  deviceDefinition: Yup.string(),
  brand: Yup.string(),
  model: Yup.string(),
  serialNumber: Yup.string(),
  tagNumber: Yup.string(),
  location: Yup.string(),
  owner: Yup.string(),
  notes: Yup.string(),
  color: Yup.string().required('Etiket rengi gereklidir'),
});
const NewRowFromKn = () => {
  const { popupStore, hospitalFilesStore, authStore } = useStore();
  const [formData, setFormData] = useState<any>(null);
  const [formHtmlString, setFormHtmlString] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const formikRef = useRef<any>(null);

  const searchByKn = async (knNumber: string) => {
    try {
      setLoading(true);
      setFormData(null);
      const response: any = await hospitalFilesStore.readFromKn(knNumber);
      if (response.value) {
        setFormHtmlString(response.value.rawResponse);
        const savedTagColor = localStorage.getItem('selectedTagColor') || 'red';
        const getNextLabelForColor = (color: string): number | '' => {
          const colorItem = hospitalFilesStore.tagColors?.find(
            x => x.color?.toLowerCase() === color?.toLowerCase()
          );
          if (colorItem && typeof colorItem.sequence === 'number') {
            return colorItem.sequence;
          }
          return '';
        };
        const newFormData = {
          labelNo: getNextLabelForColor(savedTagColor),
          deviceDefinition: response.value.hospitalRecord.biomedicalDeviceDescription || '',
          brand: response.value.hospitalRecord.brand || '',
          model: response.value.hospitalRecord.model || '',
          serialNumber: response.value.hospitalRecord.serialNumber || '',
          tagNumber: response.value.hospitalRecord.identificationNumber || '',
          location: response.value.hospitalRecord.location || '',
          owner: response.value.hospitalRecord.responsiblePerson || '',
          notes: response.value.hospitalRecord.note || '',
          color: savedTagColor, // localStorage'dan al veya varsayılan kırmızı
        };
        setFormData(newFormData);

        if (formikRef.current) {
          formikRef.current.resetForm();
          formikRef.current.setValues(newFormData);
        }
      } else {
        toast.error('Künye numarası ile kayıt bulunamadı');
      }
    } catch (error) {
      toast.error('Arama sırasında bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (values: { knNumber: string }) => {
    searchByKn(values.knNumber);
  };

  const handleSubmit = async (values: any) => {
    try {
      if (!hospitalFilesStore.selectedHospital) {
        toast.error('Lütfen bir hastane seçiniz');
        return;
      }

      if (!authStore.user) {
        toast.error('Kullanıcı bilgisi bulunamadı');
        return;
      }

      const newRecord = {
        tagId: hospitalFilesStore.selectedTag?.id,
        tagSequence: Number(values.labelNo ?? 0),
        biomedicalDeviceDescription: values.deviceDefinition,
        brand: values.brand,
        model: values.model,
        serialNumber: values.serialNumber,
        identificationNumber: values.tagNumber,
        location: values.location,
        responsiblePerson: values.owner,
        note: values.notes,
        hospitalId: hospitalFilesStore.selectedHospital.id,
        userId: authStore.user.id,
        color: values.color, 
      };

      const response: any = await hospitalFilesStore.newRowManuel(newRecord);
      if (!response.error) {
        popupStore.setIsNewKnRowOpen(false);
        hospitalFilesStore.refreshSelectedTag();
        toast.success('Kayıt başarıyla eklendi');
      } else {
        toast.error(response.message || 'Kayıt Eklenemedi');
      }
    } catch (error) {
      console.error('Form gönderim hatası:', error);
      toast.error('Kayıt eklenirken bir hata oluştu');
    }
  };

  const handleClose = () => {
    popupStore.setIsNewKnRowOpen(false);
    setFormData(null);
  };

  const handleSuggestionSearch = async (fieldName: string, searchTerm: string) => {
    return hospitalFilesStore.searchFieldSuggestions(fieldName, searchTerm);
  };

  return (
    <div>
      <Popup
        isOpen={popupStore.isNewKnRowOpen}
        onClose={handleClose}
        title="Künye Numarası ile Veri Girişi"
      >
        <div className={styles.container}>
          <div className={styles.searchContainer}>
            <Formik
              initialValues={{ knNumber: '' }}
              validationSchema={validationSchema}
              onSubmit={handleSearch}
            >
              {({ isSubmitting }) => (
                <Form className={styles.searchForm}>
                  <FormikTextField
                    name="knNumber"
                    label="Künye Numarası"
                    placeholder="Künye numarası giriniz"
                  />
                  <CommonButton type="primary" loading={loading} disabled={loading}>
                    Ara
                  </CommonButton>
                </Form>
              )}
            </Formik>
          </div>

          <div className={styles.formContainer}>
            {formData && (
              <>
                <div style={{ marginBottom: '10px' }}>
                  <CommonButton
                    type="primary"
                    onClick={() => popupStore.setIsQrCodeOriginalDataOpen(true)}
                  >
                    Orijinal Veriyi Göster
                  </CommonButton>
                </div>
                <Formik
                  innerRef={formikRef}
                  initialValues={formData}
                  validationSchema={formValidationSchema}
                  onSubmit={handleSubmit}
                  enableReinitialize={true}
                >
                  {({ isSubmitting, setFieldValue }) => (
                    <Form className={styles.form}>
                      <div className={styles.inputContainer}>
                        <FormikDropdown
                          name="color"
                          label="Etiket Rengi"
                          placeholder="Etiket rengi seçiniz"
                          options={[
                            { value: 'red', label: 'Kırmızı' },
                            { value: 'yellow', label: 'Sarı' },
                            { value: 'green', label: 'Yeşil' },
                          ]}
                          onChange={(value: string | number) => {
                            const v = (value as string) || 'red';
                            const colorItem = hospitalFilesStore.tagColors?.find(
                              x => x.color?.toLowerCase() === v.toLowerCase()
                            );
                            const next =
                              colorItem && typeof colorItem.sequence === 'number'
                                ? colorItem.sequence + 1
                                : '';
                            setFieldValue('labelNo', next);
                            localStorage.setItem('selectedTagColor', v);
                          }}
                        />
                      </div>
                      <div className={styles.inputContainer}>
                        <FormikTextField name="labelNo" label="Etiket No" type="number" />
                      </div>
                      <div className={styles.inputContainer}>
                        <FormikTextField
                          name="deviceDefinition"
                          label="Biyomedikal Cihaz Tanımı"
                          fieldToSearch="biomedicalDeviceDescription"
                          onSuggestionSearch={handleSuggestionSearch}
                        />
                      </div>
                      <div className={styles.inputContainer}>
                        <FormikTextField
                          name="brand"
                          label="Marka"
                          fieldToSearch="brand"
                          onSuggestionSearch={handleSuggestionSearch}
                        />
                      </div>
                      <div className={styles.inputContainer}>
                        <FormikTextField
                          name="model"
                          label="Model"
                          fieldToSearch="model"
                          onSuggestionSearch={handleSuggestionSearch}
                        />
                      </div>
                      <div className={styles.inputContainer}>
                        <FormikTextField name="serialNumber" label="Seri Numarası" />
                      </div>
                      <div className={styles.inputContainer}>
                        <FormikTextField name="tagNumber" label="Künye Numarası" />
                      </div>
                      <div className={styles.inputContainer}>
                        <FormikTextField
                          name="location"
                          label="Bulunduğu Yer"
                          fieldToSearch="location"
                          onSuggestionSearch={handleSuggestionSearch}
                        />
                      </div>
                      <div className={styles.inputContainer}>
                        <FormikTextField
                          name="owner"
                          label="Zimmet Sahibi"
                          fieldToSearch="responsiblePerson"
                          onSuggestionSearch={handleSuggestionSearch}
                        />
                      </div>
                      <div className={styles.inputContainer}>
                        <FormikTextField name="notes" label="Not" />
                      </div>
                      <div className={styles.buttonContainer}>
                        <CommonButton type="primary" disabled={isSubmitting} loading={isSubmitting}>
                          Kaydet
                        </CommonButton>
                      </div>
                    </Form>
                  )}
                </Formik>
              </>
            )}
          </div>
        </div>
      </Popup>
      {popupStore.isQrCodeOriginalDataOpen && <QrOriginalData data={formHtmlString} />}
    </div>
  );
};

export default observer(NewRowFromKn);
